import {canOperateDevice} from '@/lib/permissions';
import PressureVirtualValues from '@/components/PressureVirtualValues';
import PressureTravel from '@/components/PressureTravel';
import PressureCycles from '@/components/PressureCycles';
import {intervalMs} from '@/lib/pressure-interval.mjs';
import PressureTrend from '@/components/PressureTrend';
import PressureInstallationWizard from '@/pages/PressureInstallationWizard';
import {useLang} from '@/context/LanguageContext';
import {STATUS,fmtRelative} from '@/lib/format';
import {usePressureText} from '@/lib/pressure-text';



import { useState, useEffect, useCallback, useRef } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import { api } from '@/lib/api';

import { useAuth } from '@/context/AuthContext';

import { useRefresh } from '@/context/RefreshContext';

import { Button } from '@/components/ui/button';

import { Input } from '@/components/ui/input';

import { Textarea } from '@/components/ui/textarea';

import { Badge } from '@/components/ui/badge';

import { ArrowLeft, Activity, Wifi, WifiOff, Settings2, Download, CheckCircle2, AlertTriangle, MapPin, Hash, Cpu, ShieldCheck, RefreshCw } from 'lucide-react';

import { toast } from 'sonner';

import { pget, pwrite, pressureError, number as formatNumber, localTime, colors } from '@/lib/pressure';

import { Field, ChannelEditor, VirtualEditor, selectClass } from '@/components/PressureEditors';

const box = 'rounded-lg border border-border bg-card p-5';

const iso = x => new Date(x).toISOString();

export default function PressureDevice({

  device: initialDevice,

  installation = false

}) {

  const {

    pt,

    locale

  } = usePressureText();

  const {t,lang}=useLang();
  const number = value => formatNumber(value, locale);

  const {

      user

    } = useAuth(),

    {

      tick

    } = useRefresh(),

    navigate = useNavigate(),

    location = useLocation();

  const [device, setDevice] = useState(initialDevice),

    [config, setConfig] = useState(null),

    [live, setLive] = useState(null),

    [from, setFrom] = useState(localTime(Date.now() - 3600000)),

    [to, setTo] = useState(localTime(Date.now() + 60000)),

    [windowMs,setWindowMs] = useState(3600000),

    [events, setEvents] = useState([]),

    [cursor, setCursor] = useState(null),

    [selected, setSelected] = useState(null),

    [label, setLabel] = useState(''),

    [notes, setNotes] = useState(''),

    [start, setStart] = useState(localTime(Date.now() - 30000)),

    [end, setEnd] = useState(localTime(Date.now())),

    [busy, setBusy] = useState(false);

  const periodRequest=useRef(0),periodFlight=useRef(null),periodOwner=useRef(null),periodCompleted=useRef(null);
  const [periodScope,setPeriodScope]=useState(0);
  const [trendSnapshot,setTrendSnapshot]=useState(null);
  const [trendPreparation,setTrendPreparation]=useState(null);
  periodOwner.current=device.id+'|'+periodScope;
  useEffect(()=>()=>{periodOwner.current=null;periodRequest.current++;},[]);
  const edit = ['admin', 'manager'].includes(user?.role);
  const canWrite = edit && canOperateDevice(user,device);

  const path = s => `/devices/${device.id}/${s}`;

  const load = useCallback(async () => {

    try {

      const [d, c, l] = await Promise.all([api.get(`/devices/${initialDevice.id}`), pget(`/devices/${initialDevice.id}/config`), pget(`/devices/${initialDevice.id}/live`)]);

      setDevice(d.data);

      setConfig(previous => previous?.config_version === c?.config_version ? previous : c);

      setLive(l);

    } catch (e) {

      toast.error(pressureError(e));

    }

  }, [initialDevice.id]);

  useEffect(() => {

    load();

  }, [load, tick]);

  const loadPeriod = useCallback((more = false, nextCursor = null) => {
    const owner=device.id+'|'+periodScope;
    if(periodFlight.current?.owner===owner)return periodFlight.current.promise;
    if(!more&&Date.parse(to)-Date.parse(from)>=86400000&&periodCompleted.current?.owner===owner&&Date.now()-periodCompleted.current.at<60000)return Promise.resolve();
    const request=++periodRequest.current;
    const current=()=>request===periodRequest.current&&periodOwner.current===owner;
    let preparing=false;
    const flight={owner,promise:null};periodFlight.current=flight;
    flight.promise=(async()=>{
      try {
        const query=`from=${encodeURIComponent(iso(from))}&to=${encodeURIComponent(iso(to))}`;
        const seconds=(Date.parse(to)-Date.parse(from))/1000;
        await Promise.allSettled([
          pget(`/devices/${device.id}/trend?${query}&bucket_seconds=${Math.max(1,Math.ceil(seconds/400))}`)
            .then(result=>{if(current()){if(result.preparing){preparing=true;setTrendPreparation({scope:periodScope,...result});}else{setTrendPreparation(null);setTrendSnapshot({scope:periodScope,from,to,items:result.items});}}})
            .catch(error=>{if(current())toast.error(pressureError(error));}),
          pget(`/devices/${device.id}/events?${query}&limit=40`+(more&&nextCursor?'&cursor='+encodeURIComponent(nextCursor):''))
            .then(result=>{if(current()){setEvents(old=>more?[...old,...result.items]:result.items);setCursor(result.cursor);}})
            .catch(error=>{if(current())toast.error(pressureError(error));})
        ]);
      }catch(error){if(current())toast.error(pressureError(error));}
      finally{if(periodFlight.current===flight){periodFlight.current=null;periodCompleted.current=preparing?null:{owner,at:Date.now()};}}
    })();
    return flight.promise;
  },[device.id,from,to,periodScope]);

  useEffect(()=>{loadPeriod();},[loadPeriod,tick]);

  useEffect(()=>{if(windowMs){setFrom(localTime(Date.now()-windowMs));setTo(localTime(Date.now()+1000));}},[tick,windowMs]);

  const saved = c => {

    setConfig(c);

    load();

  };

  const inspect = async id => {

    try {

      setSelected(await pget(path('events/' + id)));

    } catch (e) {

      toast.error(pressureError(e));

    }

  };

  const saveLabel = async value => {

    try {

      await pwrite(path('events/' + selected.event_id + '/labels'), 'POST', {

        label: value,

        notes

      });

      await inspect(selected.event_id);

      setNotes('');

      const key = 'thrm-pressure-labels-' + device.organization_id;

      const recent = JSON.parse(localStorage.getItem(key) || '[]');

      localStorage.setItem(key, JSON.stringify([...new Set([value, ...recent])].slice(0, 6)));

      toast.success(pt("Label bewaard."));

    } catch (e) {

      toast.error(pressureError(e));

    }

  };

  const manual = async () => {

    try {

      const e = await pwrite(path('events'), 'POST', {

        start_at: iso(start),

        end_at: iso(end),

        channel_ids: enabled.filter(id => config.channels.some(c => c.channel_id === id))

      });

      await loadPeriod();

      await inspect(e.event_id);

      toast.success(pt("Interval vastgelegd."));

    } catch (e) {

      toast.error(pressureError(e));

    }

  };

  const status=STATUS[device.status_color]||STATUS.grey;
  const pending=device.installation&&device.installation.status!=='completed';
  const periodOptions=[[t('15 min','15 min'),900000],[t('1 uur','1 hour'),3600000],[t('24 uur','24 hours'),86400000],[t('7 dagen','7 days'),604800000]];
  const choosePeriod=ms=>{setPeriodScope(v=>v+1);setWindowMs(ms);if(ms){setFrom(localTime(Date.now()-ms));setTo(localTime(Date.now()+1000));}};
  const refresh=async()=>{setBusy(true);try{await load();await loadPeriod();}finally{setBusy(false);}};
  if (installation) return <PressureInstallationWizard device={device}/>;
  return <div className="mx-auto max-w-7xl space-y-6" data-testid="pressure-device-page">
    <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground" onClick={()=>navigate('/')}><ArrowLeft className="h-4 w-4"/>{t('Terug naar dashboard','Back to dashboard')}</Button>
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"><div className="min-w-0"><div className="flex flex-wrap items-center gap-3"><h1 className="font-heading text-3xl font-black tracking-tight">{device.name}</h1><Badge variant="outline" className={`gap-1.5 border-0 ${status.bg} ${status.text}`}><span className={`h-1.5 w-1.5 rounded-full ${status.dot}`}/>{!device.online?t('Geen recente verbinding','No recent connection'):device.status_color==='orange'?t('Werkdruk overschreden','Working pressure exceeded'):t('Continu meten','Continuous measurement')}</Badge></div><p className="mt-1 text-sm text-muted-foreground">{device.description||t('Geen omschrijving','No description')}</p><div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground"><span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5"/>{device.location||'—'}</span>{device.thrm_serial_number&&<span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5"/>{device.thrm_serial_number}</span>}<span className="flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5"/>{config?.channels.length||0} {t('drukkanalen','pressure channels')}</span><span className="flex items-center gap-1.5">{device.online?<Wifi className="h-3.5 w-3.5 text-emerald-500"/>:<WifiOff className="h-3.5 w-3.5 text-slate-400"/>}{t('Laatste verbinding','Last connection')}: {fmtRelative(device.last_seen,lang)}</span></div><div className="mt-3 rounded-lg border border-border bg-muted/15 px-3 py-2 text-xs"><b>Pressure Monitor</b> · {t('Meetinterval ingesteld','Configured interval')} {number(intervalMs(config?.sample_rate_hz))} ms · {t('loggerinterval gemeten','measured logger interval')} {number(intervalMs(live?.actual_sample_rate_hz))} ms</div></div>
    <div className="flex w-full flex-col items-start gap-2 lg:w-auto lg:items-end"><div className="flex flex-wrap gap-2">{user?.role==='admin'&&<Button variant="outline" className="gap-2" onClick={()=>navigate(`/devices/${device.id}/installation`)}><ShieldCheck className="h-4 w-4"/>{pending?t('Installatie hervatten','Resume installation'):t('Installatie controleren','Check installation')}</Button>}<Button variant="outline" onClick={refresh} disabled={busy} className="gap-2"><RefreshCw className={`h-4 w-4 ${busy?'animate-spin':''}`}/>{t('Vernieuwen','Refresh')}</Button></div><div className="flex w-full flex-nowrap justify-end gap-1.5 overflow-x-auto pb-1" data-testid="pressure-period-select">{periodOptions.map(([label,ms])=><button key={ms} onClick={()=>choosePeriod(ms)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 ${windowMs===ms?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'}`}>{label}</button>)}<button onClick={()=>choosePeriod(null)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${windowMs===null?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground'}`}>{t('Eigen periode','Custom period')}</button></div>{windowMs===null&&<div className="grid w-full gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-2"><Field label={t('Begin','From')}><Input type="datetime-local" step="1" value={from} onChange={e=>{setPeriodScope(v=>v+1);setFrom(e.target.value);}}/></Field><Field label={t('Eind','To')}><Input type="datetime-local" step="1" value={to} onChange={e=>{setPeriodScope(v=>v+1);setTo(e.target.value);}}/></Field></div>}</div></div>
    {user?.role==='admin'&&pending&&<div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm" data-testid="pressure-installation-warning"><div><div className="font-semibold text-amber-700 dark:text-amber-300">{t('Installatie nog niet vrijgegeven','Installation not yet released')}</div><div className="text-xs text-muted-foreground">{t(`De beheerder kan verdergaan bij stap ${device.installation.current_step} van ${device.installation.total_steps||15}.`,`The administrator can continue at step ${device.installation.current_step} of ${device.installation.total_steps||15}.`)}</div></div><Button size="sm" onClick={()=>navigate(`/devices/${device.id}/installation`)}>{t('Wizard hervatten','Resume wizard')}</Button></div>}
    <section className="space-y-2"><h2 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t('Actuele waarden','Current values')}</h2>
<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">{(config?.channels || []).flatMap((ch, i) => {

          const s = live?.samples.find(s => s.channel_id === ch.channel_id),

            alarm = s?.engineering_value > ch.working_pressure_bar;

          return [<div key={ch.channel_id} className="min-w-0 rounded-lg border border-border bg-card px-4 py-3" style={{

            borderTop: `3px solid ${colors[i]}`

          }}><div className="text-sm text-muted-foreground">{ch.channel_id} · {ch.name}</div><div className="my-2 font-mono text-3xl font-semibold">{number(s?.engineering_value)}<span className="ml-2 text-base text-muted-foreground">bar</span></div><Badge variant={alarm ? 'destructive' : 'secondary'}>{alarm ? pt("Boven werkdruk") : s?.quality || pt("Geen meting")}</Badge><p className="mt-3 text-sm text-muted-foreground">{pt("Werkdruk")} {number(ch.working_pressure_bar)} bar<br />{s ? new Date(s.timestamp).toLocaleString(locale) : pt("Wacht op eerste sample")}</p></div>,<PressureTravel key={ch.channel_id+'-travel'} deviceId={device.id} channel={ch.channel_id}/>];

        })}<PressureVirtualValues config={config} live={live}/></div>{!config?.channels.length&&<p className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">{t('Nog geen sensoren ingesteld. Een beheerder kan de installatie hervatten.','No sensors configured yet. An administrator can resume installation.')}</p>}</section>

<div className={box}><div className="flex flex-wrap justify-between gap-3"><h2 className="text-xl font-bold">{pt("Drukverloop")}</h2><span className="text-sm text-muted-foreground">{t('Meetinterval','Sampling interval')} {number(intervalMs(config?.sample_rate_hz))} ms · {t('gemeten loggerinterval','measured logger interval')} {number(intervalMs(live?.actual_sample_rate_hz))} ms</span></div><div className="mt-5"><PressureTrend key={JSON.stringify([device.organization_id,device.id,periodScope])} organizationId={device.organization_id} deviceId={device.id} channels={config?.channels||[]} points={trendSnapshot?.scope===periodScope?trendSnapshot.items:null} from={trendSnapshot?.scope===periodScope?trendSnapshot.from:from} to={trendSnapshot?.scope===periodScope?trendSnapshot.to:to} preparing={trendPreparation?.scope===periodScope?trendPreparation:null} refreshTick={tick} canManageDemo={user.role==='admin'} canReadRaw={edit} onZoom={(left,right)=>{setPeriodScope(v=>v+1);setWindowMs(null);setFrom(localTime(left));setTo(localTime(right));}} onEvent={canWrite?(left,right)=>{setStart(localTime(left));setEnd(localTime(right));document.getElementById('pressure-events')?.scrollIntoView({behavior:'smooth'});}:null} onInspectEvent={id=>{inspect(id);document.getElementById('pressure-events')?.scrollIntoView({behavior:'smooth'});}}/></div><div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{config?.channels.map(ch => {

            const rows = (trendSnapshot?.scope===periodScope?trendSnapshot.items:[]).filter(p => p.channel_id === ch.channel_id && p.count),

              n = rows.reduce((s, r) => s + r.count, 0);

            return <div key={ch.channel_id} className="rounded-lg bg-muted/30 p-3 text-sm"><b>{ch.channel_id}</b><p>Min {n ? number(Math.min(...rows.map(r => r.min))) : '—'} · max {n ? number(Math.max(...rows.map(r => r.max))) : '—'}</p><p>{n} {t('metingen','samples')}</p></div>;

          })}</div></div>

<PressureCycles key={device.id} deviceId={device.id} channels={config?.channels||[]} from={from} to={to} admin={user.role==='admin'}/>

<div className="grid gap-5 xl:grid-cols-3"><div className={box + ' xl:col-span-2'}><h2 className="text-xl font-bold">{pt("Afgeleide waarden")}</h2><div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{Object.entries(live?.virtual || {}).map(([id, v]) => <div key={id}><p className="text-sm text-muted-foreground">{config?.virtual_channels.find(c => c.channel_id === id)?.name || id}</p><div className="my-2 font-mono text-2xl font-semibold">{number(v.value)} <span className="text-sm">{v.unit}</span></div>{v.reason && <p className="text-sm text-amber-600">{v.reason}</p>}{v.assumptions?.map((a, i) => <p key={i} className="text-sm text-muted-foreground">{pt("Aangenomen")} {a.value} {a.unit}: {a.source}</p>)}</div>)}</div>{!config?.virtual_channels.length && <p className="mt-3 text-sm text-muted-foreground">{pt("Nog geen berekeningen ingesteld.")}</p>}</div>{user?.role === 'admin' && <div className={box}><h2 className="font-bold">{pt("Loggerstatus")}</h2><dl className="mt-4 space-y-3 text-sm"><div>{pt("Configuratie: v")}{config?.config_version || 0} · {device.receipt?.status || pt("nog niet bevestigd")}</div><div>{pt("Uploadwachtrij:")} {live?.heartbeat?.outbox_pending ?? '—'} batches</div><div>{pt("Vrije opslag:")} {number(live?.heartbeat?.storage_free_bytes == null ? null : live.heartbeat.storage_free_bytes / 1048576)} MiB</div><div>{pt("Klok:")} {live?.heartbeat?.clock_synced ? pt("gesynchroniseerd") : pt("niet bevestigd")}</div><div className="break-words">Software: {live?.heartbeat?.software_version || pt("nog niet verbonden")}</div></dl></div>}</div>

<section id="pressure-events" className="scroll-mt-24 space-y-3"><h2 className="font-heading text-xl font-black tracking-tight">{t('Gebeurtenissen en labels','Events and labels')}</h2><div className="grid gap-5 xl:grid-cols-[1fr_1.2fr]"><div className={box}><h2 className="font-heading text-base font-bold">{t('Vastgelegde intervallen','Captured intervals')}</h2><p className="mt-1 text-sm text-muted-foreground">{pt("Periode")} {new Date(from).toLocaleString(locale)} – {new Date(to).toLocaleString(locale)}</p><div className="mt-4 space-y-2">{events.map(e => <button className={`w-full rounded-lg border p-4 text-left ${selected?.event_id === e.event_id ? 'border-primary bg-primary/5' : 'border-border'}`} key={e.event_id} onClick={() => inspect(e.event_id)}><div className="flex items-center justify-between gap-2"><b>{new Date(e.trigger_at).toLocaleString(locale)}</b><Badge variant="secondary">{e.status}</Badge></div><p className="mt-2 text-sm text-muted-foreground">{e.kind === 'manual' ? pt("Handmatig interval") : e.continuation_of ? pt("Vervolgsegment") : pt("Werkdruktrigger")} · {e.channel_ids.join(', ')} · {e.triggers.length} {pt("triggervermeldingen")}</p></button>)}{!events.length && <p className="py-6 text-sm">{pt("Geen captures in deze periode.")}</p>}</div>{cursor && <Button variant="outline" className="mt-4" onClick={() => loadPeriod(true, cursor)}>{pt("Meer laden")}</Button>}</div><div className="space-y-5">{canWrite && config && <div className={box}><h3 className="font-bold">{pt("Handmatig interval")}</h3><div className="my-4 grid gap-3 sm:grid-cols-2"><Field label={pt("Begin event")}><Input type="datetime-local" step="1" value={start} onChange={e => setStart(e.target.value)} /></Field><Field label={pt("Eind event")}><Input type="datetime-local" step="1" value={end} onChange={e => setEnd(e.target.value)} /></Field></div><Button onClick={manual}>{pt("Interval bewaren")}</Button></div>}{selected && <div className={box}><h3 className="font-bold">{pt("Eventdetails")}</h3><p className="my-3 break-words text-sm">{selected.start_at} → {selected.end_at || pt("nog open")}<br />{pt("Ontbrekende voorgeschiedenis:")} {selected.missing_pre_seconds} {pt("s \xB7 configuratie v")}{selected.config_version}</p>{selected.continuation_of && <p className="mb-3 break-words text-sm">{pt("Vervolg van")} {selected.continuation_of}{pt(". Oorspronkelijke triggers tellen niet als nieuwe overschrijding.")}</p>}{selected.labels.map((l, i) => <div key={i} className="mb-2 rounded-lg bg-muted/40 p-3"><b>{l.label}</b><p className="text-sm">{l.notes}</p><small>{new Date(l.created_at).toLocaleString(locale)}</small></div>)}{canWrite && config?.features.event_labels && <><div className="my-4 flex flex-wrap gap-2">{[...new Set([pt("Heffen"), pt("Dalen"), pt("Rijden"), pt("Stilstand"), ...JSON.parse(localStorage.getItem('thrm-pressure-labels-' + device.organization_id) || '[]')])].map(l => <Button key={l} size="lg" variant="outline" onClick={() => saveLabel(l)}>{l}</Button>)}</div><Field label={pt("Eigen label")}><Input value={label} maxLength={80} onChange={e => setLabel(e.target.value)} /></Field><div className="my-3"><Field label={pt("Notities")}><Textarea value={notes} onChange={e => setNotes(e.target.value)} /></Field></div><Button disabled={!label.trim()} onClick={() => saveLabel(label)}>{pt("Label bewaren")}</Button></>}</div>}</div></div></section>

{canWrite&&<section className="space-y-4 border-t border-border pt-6" data-testid="pressure-device-management"><div><h2 className="font-heading text-xl font-black tracking-tight">{t('Apparaatbeheer','Device management')}</h2><p className="mt-1 text-sm text-muted-foreground">{t('Beheer sensoren, werkdruk en berekende drukwaarden.','Manage sensors, working pressure and calculated pressure values.')}</p></div><details className="rounded-lg border border-border bg-card"><summary className="cursor-pointer px-5 py-4 font-heading text-base font-bold">{t('Sensoren en kanalen','Sensors and channels')}</summary><div className="border-t border-border p-5"><ChannelEditor device={device} config={config} onSaved={saved}/></div></details><details className="rounded-lg border border-border bg-card"><summary className="cursor-pointer px-5 py-4 font-heading text-base font-bold">{t('Afgeleide waarden instellen','Configure calculated values')}</summary><div className="border-t border-border p-5"><VirtualEditor device={device} config={config} onSaved={saved}/></div></details></section>}

<p className="text-sm text-muted-foreground">{pt("Operation recognition, normal reference en deviation detection zijn niet beschikbaar. Basislogging blijft altijd actief.")}</p></div>;

}


